import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { putForecastAnnualTarget } from '../lib/api';
import { formatNumber, formatPercent } from '../lib/utils';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';

function paceGapClass(value) {
  if (value === null || value === undefined) return 'text-gray-400';
  // Positive gap = behind year-to-date pace; negative = ahead.
  return Number(value) > 0 ? '!text-purple-700' : Number(value) < 0 ? '!text-green-700' : 'text-gray-800';
}

function Metric({ label, value, valueClassName = 'text-gray-800' }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className={`mt-0.5 font-mono text-sm tabular-nums ${valueClassName}`}>{value}</dd>
    </div>
  );
}

export function ForecastAnnualTargetCard({ data, onSaved }) {
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editOpen) {
      const current = data?.annual_sales_target_chf;
      setDraft(current != null && current > 0 ? String(current) : '');
    }
  }, [editOpen, data?.annual_sales_target_chf]);

  if (!data?.applicable) return null;

  const hasTarget = data.annual_sales_target_chf != null && data.annual_sales_target_chf > 0;

  const handleSave = async () => {
    const parsed = Number(String(draft).replace(/'/g, '').replace(/\s/g, ''));
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error('Enter a valid non-negative target amount');
      return;
    }
    setSaving(true);
    try {
      await putForecastAnnualTarget({ year: data.year, annual_sales_target_chf: parsed });
      toast.success('Annual target saved');
      setEditOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save annual target');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:rounded-lg sm:p-4"
        data-testid="forecast-annual-target-card"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Annual target</p>
            {data.time_elapsed_pct != null ? (
              <p className="mt-0.5 text-[11px] text-gray-400">
                {formatPercent(data.time_elapsed_pct)} of {data.year} elapsed
              </p>
            ) : null}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => setEditOpen(true)}
            data-testid="forecast-annual-target-edit"
          >
            Edit target
          </Button>
        </div>

        {!hasTarget ? (
          <p className="mt-3 text-sm text-gray-500">
            No annual sales target set for {data.year}. Click Edit target to add one.
          </p>
        ) : (
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-6">
            <Metric label="Target" value={`${formatNumber(data.annual_sales_target_chf)} CHF`} />
            <Metric
              label="Achieved committed"
              value={data.achieved_committed_pct != null ? formatPercent(data.achieved_committed_pct) : '—'}
            />
            <Metric
              label="Forecast coverage"
              value={data.forecast_coverage_pct != null ? formatPercent(data.forecast_coverage_pct) : '—'}
            />
            <Metric
              label="Expected by today"
              value={
                data.expected_target_by_today_chf != null
                  ? `${formatNumber(data.expected_target_by_today_chf)} CHF`
                  : '—'
              }
            />
            <Metric
              label="Gap vs committed"
              value={
                data.gap_vs_expected_committed_chf != null
                  ? `${formatNumber(data.gap_vs_expected_committed_chf)} CHF`
                  : '—'
              }
              valueClassName={paceGapClass(data.gap_vs_expected_committed_chf)}
            />
            <Metric
              label="Gap vs forecast"
              value={
                data.gap_vs_expected_forecast_chf != null
                  ? `${formatNumber(data.gap_vs_expected_forecast_chf)} CHF`
                  : '—'
              }
              valueClassName={paceGapClass(data.gap_vs_expected_forecast_chf)}
            />
          </dl>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Annual sales target ({data.year})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs font-medium text-gray-600" htmlFor="annual-target-input">
              Target amount (CHF)
            </label>
            <Input
              id="annual-target-input"
              type="number"
              min="0"
              step="1000"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="e.g. 300000"
              data-testid="forecast-annual-target-input"
            />
            <p className="text-xs text-gray-500">
              Applies to {data.year} only. Pace KPIs use days elapsed in the calendar year.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving} data-testid="forecast-annual-target-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
